import './styles/blogArchives.scss';

import {
    Breadcrumb,
    Button,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import {
    EnglanderButton,
    ImageBlock,
    Pagination,
    SingleBlog,
} from '../components/Primitives';
import React, { useRef, useState } from 'react';

import { IBlog } from '../interfaces/interfaces';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import arrowIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Right-Arrow-Icon.svg';
import { graphql } from 'gatsby';
import { scrollToRef } from '../utils';
import { useEffect } from 'react';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query {
        listingCms: allProductsJson(
            filter: { code: { eq: "englander_blogs" } }
        ) {
            nodes {
                ...cmsDataFragment
            }
        }
        blogs: allProductsJson(
            filter: { category: { eq: "/Englander/CMS/Blog/" } }
        ) {
            nodes {
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    content {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                        valuesMarkdownRemark {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    content_short {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                        valuesMarkdownRemark {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    blog_subject {
                        group
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    recent_article {
                        values {
                            fr_CA
                            en_CA
                            en_US
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
                fields {
                    ...productsFieldsImagesFragment
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
            }
        }
        metadata: allProductsJson(filter: { code: { eq: "englander_blogs" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        faqBlock: allProductsJson(
            filter: { code: { eq: "englander_contact-page-faq" } }
        ) {
            nodes {
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                description {
                    valuesMarkdownRemark {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
            }
        }
    }
`;

const BlogArchives = ({ data }) => {
    const { t } = useTranslation('pages');
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const faqBlock = data?.faqBlock?.nodes[0];
    const [page, setPage] = useState(1);
    const pageTitle = data?.listingCms?.nodes?.[0]?.title?.[currentLocale];
    const blogsPerPage = 9;
    const blogs: IBlog[] = data?.blogs?.nodes;
    let categories = blogs.map(
        (blog) => blog.attributes?.blog_subject?.values[0]?.[currentLocale]
    );
    categories = categories.filter(
        (x, i, a) => a.indexOf(x) === i && x?.trim !== undefined
    );

    const [filteredBlogs, setFilteredBlogs] = useState(blogs);
    const recentBlog = blogs.find(
        (blog) => blog?.attributes?.recent_article !== null
    );
    const [blogCategory, setBlogCategory] = useState('all');
    const metadataAttributes = data?.metadata?.nodes?.[0]?.attributes;
    const mainBlogActionButton = {
        title: t('blogs.read-more'),
        hasArrow: true,
        onClick: () =>
            navigateTranslated(
                `/blogArchives/${
                    recentBlog?.code.replace('englander_', '') + '/'
                }`
            ),
    };
    const blogsRef = useRef(null);

    useEffect(() => {
        scrollToRef(blogsRef, 0);
    }, [page]);

    return (
        <Layout>
            <SEO
                title={
                    metadataAttributes.meta_title?.values?.[0][currentLocale]
                }
                description={
                    metadataAttributes.meta_description?.values?.[0][
                        currentLocale
                    ]
                }
            />
            <div className='blogs-titlebanner'>
                <h1 className='page-title'> {pageTitle}</h1>
            </div>

            <div className='main-blog container'>
                <Breadcrumb
                    className='blogs-breadcrumb'
                    elements={[
                        { link: '/', name: t('layout:appliance.home') },
                        {
                            link: ``,
                            name: pageTitle,
                        },
                    ]}
                />
                <div className='main-blog-content'>
                    {recentBlog && (
                        <ImageBlock
                            title={recentBlog.title?.[currentLocale]}
                            contentMarkdown={
                                recentBlog?.attributes?.content_short
                                    ?.valuesMarkdownRemark?.[0]?.[currentLocale]
                            }
                            image={
                                recentBlog?.fields?.images?.[0]?.childImageSharp
                                    ?.gatsbyImageData
                            }
                            button={mainBlogActionButton}
                        />
                    )}
                </div>
            </div>
            <div className='blog-page container'>
                <div className='blog-categories'>
                    {categories.map((category, index) => (
                        <Button
                            key={index}
                            label={category}
                            className={
                                blogCategory === category
                                    ? 'category-btn active'
                                    : 'category-btn'
                            }
                            onClick={() => {
                                setBlogCategory(category);
                                setFilteredBlogs(
                                    blogs.filter(
                                        (blog) =>
                                            blog?.attributes?.blog_subject
                                                ?.values[0]?.[currentLocale] ===
                                            category
                                    )
                                );
                            }}
                        />
                    ))}
                    <Button
                        label={t('blogs.all')}
                        className={
                            blogCategory === 'all'
                                ? 'category-btn active'
                                : 'category-btn'
                        }
                        onClick={() => {
                            setBlogCategory('all');
                            setFilteredBlogs(blogs);
                        }}
                    />
                    <div className='flexed-txt'>
                        {t('blogs.what-looking-for')}
                    </div>
                </div>

                <div className='blog-section' ref={blogsRef}>
                    {filteredBlogs &&
                        filteredBlogs.length > 0 &&
                        filteredBlogs
                            ?.slice(
                                blogsPerPage * (page - 1),
                                blogsPerPage * page
                            )
                            ?.map((blog, index) => {
                                return (
                                    <SingleBlog
                                        blog={blog}
                                        key={`blog-archives-${index}`}
                                    />
                                );
                            })}
                </div>
            </div>
            {filteredBlogs && filteredBlogs.length / blogsPerPage > 1 && (
                <Pagination
                    currentPage={page}
                    onPageChange={(newPage: number) => {
                        setPage(newPage);
                    }}
                    pageNumber={Math.ceil(filteredBlogs.length / blogsPerPage)}
                />
            )}
            <div className='page-footer-contactPage'>
                <div className='empty'></div>
                <div className='right-side'>
                    <div className='content'>
                        <div className='content-title'>
                            {faqBlock?.title?.[currentLocale]}
                        </div>
                        <div
                            className='content-text'
                            dangerouslySetInnerHTML={{
                                __html: faqBlock?.description
                                    ?.valuesMarkdownRemark?.[currentLocale],
                            }}
                        />
                    </div>
                    <div className='button-holder'>
                        <EnglanderButton
                            className='faqBtn'
                            label={t('layout:menu-titles.faq')}
                            iconRight={arrowIcon}
                            link={`/faq/wood-stoves/`}
                        />
                    </div>
                </div>
            </div>
        </Layout>
    );
};
export default BlogArchives;
